import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  Segment,
  Button,
  Menu,
  Table,
  Dimmer,
  Loader,
  Dropdown,
  Input,
  Icon,
  Tab,
  Label,
} from 'semantic-ui-react';
import {
  getAppNoticeList,
  getFAQList,
  getInquiryList,
  getPushNotificationList,
  getForbiddenWordList,
  addForbiddenWord,
  deleteForbiddenWord,
  getBlockedUserList,
  unblockUser,
} from '../api/OperationSettingsApi';
import {
  AppNoticeListDTO,
  FAQListDTO,
  InquiryListDTO,
  PushNotificationListDTO,
  ForbiddenWordListDTO,
  BlockedUserListDTO,
} from '../dto/OperationSettingsDTOs';

const OperationSettingsPage: React.FC = () => {
  // TODO: 실제 인증 토큰을 localStorage나 context에서 가져오도록 수정
  const authToken = localStorage.getItem('accessToken') || '';

  // 날짜 포맷팅 함수 (YYYY-MM-DD)
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 내일 날짜 계산
  const getTomorrow = (): string => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatDate(tomorrow);
  };

  // N일 전 날짜 계산
  const getDaysAgo = (days: number): string => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return formatDate(date);
  };

  // 오늘 날짜 계산
  const getToday = (): string => {
    return formatDate(new Date());
  };

  // 초기 날짜 계산
  const getInitialStartDate = (): string => {
    return getDaysAgo(7);
  };

  const getInitialEndDate = (): string => {
    return getTomorrow();
  };

  // 필터 관련 state
  const [startDate, setStartDate] = useState(getInitialStartDate());
  const [endDate, setEndDate] = useState(getInitialEndDate());
  const [selectedPeriod, setSelectedPeriod] = useState('7days');
  const [dateInputEnabled, setDateInputEnabled] = useState(false);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<string | undefined>('all');

  // 앱 공지 관련 state
  const [appNoticeLoading, setAppNoticeLoading] = useState(true);
  const [appNoticeData, setAppNoticeData] = useState<AppNoticeListDTO | null>(null);

  // FAQ/문의 답변 관련 state
  const [faqInquiryTab, setFaqInquiryTab] = useState<'faq' | 'inquiry'>('inquiry');
  const [faqLoading, setFaqLoading] = useState(true);
  const [faqData, setFaqData] = useState<FAQListDTO | null>(null);
  const [inquiryLoading, setInquiryLoading] = useState(true);
  const [inquiryData, setInquiryData] = useState<InquiryListDTO | null>(null);

  // 푸시 알림 관련 state
  const [pushLoading, setPushLoading] = useState(true);
  const [pushData, setPushData] = useState<PushNotificationListDTO | null>(null);

  // 금지어·차단 설정 관련 state
  const [blockSettingsTab, setBlockSettingsTab] = useState<'forbidden' | 'blocked'>('forbidden');
  const [forbiddenWordLoading, setForbiddenWordLoading] = useState(true);
  const [forbiddenWordData, setForbiddenWordData] = useState<ForbiddenWordListDTO | null>(null);
  const [newForbiddenWord, setNewForbiddenWord] = useState('');
  const [blockedUserLoading, setBlockedUserLoading] = useState(true);
  const [blockedUserData, setBlockedUserData] = useState<BlockedUserListDTO | null>(null);
  const [blockedUserSearchKeyword, setBlockedUserSearchKeyword] = useState('');

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    
    if (period === 'today') {
      setDateInputEnabled(false);
      setStartDate(getToday());
      setEndDate(getTomorrow());
    } else if (period === '7days') {
      setDateInputEnabled(false);
      setStartDate(getDaysAgo(7));
      setEndDate(getTomorrow());
    } else if (period === '30days') {
      setDateInputEnabled(false);
      setStartDate(getDaysAgo(30));
      setEndDate(getTomorrow());
    } else if (period === 'all') {
      setDateInputEnabled(true);
    }
  };

  const ageGroupOptions = [
    { key: 'all', text: '전체', value: 'all' },
    { key: '10s', text: '10대', value: '10s' },
    { key: '20s30s', text: '20,30대', value: '20s30s' },
    { key: '40s50s', text: '40,50대', value: '40s50s' },
    { key: '60s', text: '60대이상', value: '60s' },
  ];

  // 앱 공지 목록 로드
  const loadAppNoticeList = async () => {
    setAppNoticeLoading(true);
    try {
      const ageGroup = selectedAgeGroup === 'all' ? undefined : selectedAgeGroup;
      const result = await getAppNoticeList(authToken, startDate, endDate, ageGroup);
      if (result && result.errorCode === 0) {
        setAppNoticeData(result.response);
      }
    } catch (error) {
      console.error('앱 공지 목록 로드 실패:', error);
    } finally {
      setAppNoticeLoading(false);
    }
  };

  // FAQ 목록 로드
  const loadFAQList = async () => {
    setFaqLoading(true);
    try {
      const result = await getFAQList(authToken);
      if (result && result.errorCode === 0) {
        setFaqData(result.response);
      }
    } catch (error) {
      console.error('FAQ 목록 로드 실패:', error);
    } finally {
      setFaqLoading(false);
    }
  };

  // 1:1 문의 목록 로드
  const loadInquiryList = async () => {
    setInquiryLoading(true);
    try {
      const result = await getInquiryList(authToken);
      if (result && result.errorCode === 0) {
        setInquiryData(result.response);
      }
    } catch (error) {
      console.error('1:1 문의 목록 로드 실패:', error);
    } finally {
      setInquiryLoading(false);
    }
  };

  // 푸시 알림 목록 로드
  const loadPushNotificationList = async () => {
    setPushLoading(true);
    try {
      const result = await getPushNotificationList(authToken);
      if (result && result.errorCode === 0) {
        setPushData(result.response);
      }
    } catch (error) {
      console.error('푸시 알림 목록 로드 실패:', error);
    } finally {
      setPushLoading(false);
    }
  };

  // 금지어 목록 로드
  const loadForbiddenWordList = async () => {
    setForbiddenWordLoading(true);
    try {
      const result = await getForbiddenWordList(authToken);
      if (result && result.errorCode === 0) {
        setForbiddenWordData(result.response);
      }
    } catch (error) {
      console.error('금지어 목록 로드 실패:', error);
    } finally {
      setForbiddenWordLoading(false);
    }
  };

  // 차단 사용자 목록 로드
  const loadBlockedUserList = async () => {
    setBlockedUserLoading(true);
    try {
      const keyword = blockedUserSearchKeyword.trim() || undefined;
      const result = await getBlockedUserList(authToken, keyword);
      if (result && result.errorCode === 0) {
        setBlockedUserData(result.response);
      }
    } catch (error) {
      console.error('차단 사용자 목록 로드 실패:', error);
    } finally {
      setBlockedUserLoading(false);
    }
  };

  useEffect(() => {
    loadAppNoticeList();
  }, [startDate, endDate, selectedAgeGroup]);

  useEffect(() => {
    if (faqInquiryTab === 'faq') {
      loadFAQList();
    } else {
      loadInquiryList();
    }
  }, [faqInquiryTab]);

  useEffect(() => {
    loadPushNotificationList();
    loadForbiddenWordList();
    loadBlockedUserList();
  }, []);

  // 금지어 추가
  const handleAddForbiddenWord = async () => {
    if (!newForbiddenWord.trim()) return;
    try {
      await addForbiddenWord(authToken, newForbiddenWord.trim());
      setNewForbiddenWord('');
      loadForbiddenWordList();
    } catch (error) {
      console.error('금지어 추가 실패:', error);
    }
  };

  // 금지어 삭제
  const handleDeleteForbiddenWord = async (wordNo: number) => {
    try {
      await deleteForbiddenWord(authToken, wordNo);
      loadForbiddenWordList();
    } catch (error) {
      console.error('금지어 삭제 실패:', error);
    }
  };

  // 차단 사용자 해제
  const handleUnblockUser = async (blockNo: number) => {
    try {
      await unblockUser(authToken, blockNo);
      loadBlockedUserList();
    } catch (error) {
      console.error('차단 해제 실패:', error);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PUBLISHED':
        return '게시';
      case 'HIDDEN':
        return '숨김';
      case 'APPROVED':
        return '승인';
      case 'PENDING':
        return '답변대기';
      case 'COMPLETED':
        return '완료';
      case 'SCHEDULED':
        return '예약';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PUBLISHED':
      case 'APPROVED':
      case 'COMPLETED':
        return 'green';
      case 'HIDDEN':
        return 'grey';
      case 'PENDING':
        return 'orange';
      case 'SCHEDULED':
        return 'blue';
      default:
        return 'grey';
    }
  };

  const formatDateDisplay = (dateStr: string): string => {
    return dateStr.replace(/-/g, '. ');
  };

  return (
    <Container fluid>
      {/* 상단 제목 */}
      <Header as="h2" style={{ marginBottom: 16 }}>
        운영 설정
      </Header>

      {/* 상단 필터 영역 */}
      <Segment
        raised
        style={{
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#f8f9ff',
          marginBottom: 24,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* 기간 선택 */}
          <Menu compact>
            <Menu.Item
              active={selectedPeriod === 'today'}
              onClick={() => handlePeriodChange('today')}
            >
              오늘
            </Menu.Item>
            <Menu.Item
              active={selectedPeriod === '7days'}
              onClick={() => handlePeriodChange('7days')}
            >
              7일
            </Menu.Item>
            <Menu.Item
              active={selectedPeriod === '30days'}
              onClick={() => handlePeriodChange('30days')}
            >
              30일
            </Menu.Item>
            <Menu.Item
              active={selectedPeriod === 'all'}
              onClick={() => handlePeriodChange('all')}
            >
              전체
            </Menu.Item>
          </Menu>

          {/* 연령대별 드롭다운 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 500 }}>연령대별:</span>
            <Dropdown
              selection
              options={ageGroupOptions}
              value={selectedAgeGroup}
              onChange={(e, { value }) => setSelectedAgeGroup(value as string | undefined)}
              style={{ minWidth: 120 }}
            />
          </div>
        </div>
      </Segment>

      {/* 1. 앱 공지 관리 */}
      <Segment raised style={{ padding: '20px', marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Header as="h4" style={{ margin: 0 }}>
            앱 공지 관리
          </Header>
          <Button primary>
            <Icon name="plus" />
            공지사항 등록
          </Button>
        </div>

        {appNoticeLoading ? (
          <Dimmer active inverted>
            <Loader size="small">로딩 중...</Loader>
          </Dimmer>
        ) : (
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>제목</Table.HeaderCell>
                <Table.HeaderCell>등록일</Table.HeaderCell>
                <Table.HeaderCell>상태(게시/숨김)</Table.HeaderCell>
                <Table.HeaderCell>관리(수정/삭제)</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {appNoticeData?.notices.map((notice) => (
                <Table.Row key={notice.noticeNo}>
                  <Table.Cell>{notice.title}</Table.Cell>
                  <Table.Cell>{formatDateDisplay(notice.registrationDate)}</Table.Cell>
                  <Table.Cell>
                    <Label color={getStatusColor(notice.status) as any}>
                      {getStatusLabel(notice.status)}
                    </Label>
                  </Table.Cell>
                  <Table.Cell>
                    <Button size="small" basic>
                      수정/삭제
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Segment>

      {/* 2. FAQ/문의 답변 */}
      <Segment raised style={{ padding: '20px', marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Header as="h4" style={{ margin: 0 }}>
            FAQ/문의 답변
          </Header>
          <Button primary>
            <Icon name="plus" />
            FAQ 등록
          </Button>
        </div>

        <Menu tabular>
          <Menu.Item
            active={faqInquiryTab === 'faq'}
            onClick={() => setFaqInquiryTab('faq')}
          >
            FAQ 관리
          </Menu.Item>
          <Menu.Item
            active={faqInquiryTab === 'inquiry'}
            onClick={() => setFaqInquiryTab('inquiry')}
          >
            1:1 문의 답변
          </Menu.Item>
        </Menu>

        {faqInquiryTab === 'inquiry' && (
          <div style={{ 
            marginTop: 16, 
            marginBottom: 16, 
            padding: '12px 16px', 
            background: '#f5f5f5', 
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <Icon name="chat" />
            <span>미답변 문의: {inquiryData?.unansweredCount || 0}건</span>
          </div>
        )}

        {faqInquiryTab === 'faq' ? (
          faqLoading ? (
            <Dimmer active inverted>
              <Loader size="small">로딩 중...</Loader>
            </Dimmer>
          ) : (
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>제목</Table.HeaderCell>
                  <Table.HeaderCell>등록일</Table.HeaderCell>
                  <Table.HeaderCell>관리</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {faqData?.faqs.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan="3" textAlign="center">
                      등록된 FAQ가 없습니다.
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          )
        ) : (
          inquiryLoading ? (
            <Dimmer active inverted>
              <Loader size="small">로딩 중...</Loader>
            </Dimmer>
          ) : (
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>문의 유형 제목</Table.HeaderCell>
                  <Table.HeaderCell>작성자</Table.HeaderCell>
                  <Table.HeaderCell>등록일</Table.HeaderCell>
                  <Table.HeaderCell>상태(답변대기/완료)</Table.HeaderCell>
                  <Table.HeaderCell>관리(답변하기)</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {inquiryData?.inquiries.map((inquiry) => (
                  <Table.Row key={inquiry.inquiryNo}>
                    <Table.Cell>{inquiry.inquiryTypeTitle}</Table.Cell>
                    <Table.Cell>{inquiry.author}</Table.Cell>
                    <Table.Cell>{formatDateDisplay(inquiry.registrationDate)}</Table.Cell>
                    <Table.Cell>
                      <Label color={getStatusColor(inquiry.status) as any}>
                        {getStatusLabel(inquiry.status)}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      <Button size="small" basic>
                        답변하기
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )
        )}
      </Segment>

      {/* 3. 푸시 알림 관리 */}
      <Segment raised style={{ padding: '20px', marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Header as="h4" style={{ margin: 0 }}>
            푸시 알림 관리
          </Header>
          <Button primary>
            <Icon name="plus" />
            새 푸시 등록
          </Button>
        </div>

        {pushLoading ? (
          <Dimmer active inverted>
            <Loader size="small">로딩 중...</Loader>
          </Dimmer>
        ) : (
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>제목</Table.HeaderCell>
                <Table.HeaderCell>발송 내용</Table.HeaderCell>
                <Table.HeaderCell>발송 일시(예약/완료)</Table.HeaderCell>
                <Table.HeaderCell>타겟</Table.HeaderCell>
                <Table.HeaderCell>상태</Table.HeaderCell>
                <Table.HeaderCell>관리</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {pushData?.notifications.map((notification) => (
                <Table.Row key={notification.pushNo}>
                  <Table.Cell>{notification.title}</Table.Cell>
                  <Table.Cell>{notification.content}</Table.Cell>
                  <Table.Cell>{formatDateDisplay(notification.sendDateTime)}</Table.Cell>
                  <Table.Cell>{notification.target}</Table.Cell>
                  <Table.Cell>
                    <Label color={getStatusColor(notification.status) as any}>
                      {getStatusLabel(notification.status)}
                    </Label>
                  </Table.Cell>
                  <Table.Cell>
                    <Button size="small" basic>
                      관리
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Segment>

      {/* 4. 금지어·차단 설정 */}
      <Segment raised style={{ padding: '20px', marginBottom: 24 }}>
        <Header as="h4" style={{ marginBottom: 16, marginTop: 0 }}>
          금지어·차단 설정
        </Header>

        <Menu tabular>
          <Menu.Item
            active={blockSettingsTab === 'forbidden'}
            onClick={() => setBlockSettingsTab('forbidden')}
          >
            금지어 관리
          </Menu.Item>
          <Menu.Item
            active={blockSettingsTab === 'blocked'}
            onClick={() => setBlockSettingsTab('blocked')}
          >
            차단 사용자 관리
          </Menu.Item>
        </Menu>

        {blockSettingsTab === 'forbidden' ? (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <Input
                placeholder="금지어 관리"
                value={newForbiddenWord}
                onChange={(e) => setNewForbiddenWord(e.target.value)}
                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') {
                    handleAddForbiddenWord();
                  }
                }}
                style={{ flex: 1 }}
              />
              <Button primary onClick={handleAddForbiddenWord}>
                <Icon name="plus" />
                추가
              </Button>
            </div>

            {forbiddenWordLoading ? (
              <Dimmer active inverted>
                <Loader size="small">로딩 중...</Loader>
              </Dimmer>
            ) : (
              <div>
                {forbiddenWordData?.words.map((word) => (
                  <div
                    key={word.wordNo}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 4,
                      marginBottom: 8,
                    }}
                  >
                    <span>{word.word}</span>
                    <Button
                      size="small"
                      color="red"
                      onClick={() => handleDeleteForbiddenWord(word.wordNo)}
                    >
                      삭제
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <Input
                icon="search"
                iconPosition="left"
                placeholder="차단 사유"
                value={blockedUserSearchKeyword}
                onChange={(e) => setBlockedUserSearchKeyword(e.target.value)}
                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') {
                    loadBlockedUserList();
                  }
                }}
                style={{ flex: 1 }}
              />
            </div>

            {blockedUserLoading ? (
              <Dimmer active inverted>
                <Loader size="small">로딩 중...</Loader>
              </Dimmer>
            ) : (
              <Table celled>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>사용자ID</Table.HeaderCell>
                    <Table.HeaderCell>차단 사유</Table.HeaderCell>
                    <Table.HeaderCell>차단일</Table.HeaderCell>
                    <Table.HeaderCell>관리(해제)</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {blockedUserData?.users.map((user) => (
                    <Table.Row key={user.blockNo}>
                      <Table.Cell>{user.userId}</Table.Cell>
                      <Table.Cell>{user.blockReason}</Table.Cell>
                      <Table.Cell>{formatDateDisplay(user.blockDate)}</Table.Cell>
                      <Table.Cell>
                        <Button
                          size="small"
                          basic
                          onClick={() => handleUnblockUser(user.blockNo)}
                        >
                          해제
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}
          </div>
        )}
      </Segment>
    </Container>
  );
};

export default OperationSettingsPage;
